import { currentLocale } from './current'

/**
 * Numbers, dates and relative times for people, in the interface language. Use these instead of
 * moment's format() or toLocaleString() with a hardcoded 'en'. Money lives in utils/currency.ts
 * (formatMoney), which follows the interface language too.
 *
 * Every helper takes an optional last `locale` argument, mainly for tests.
 */

type DateInput = Date | string | number

const toDate = (value: DateInput): Date => (value instanceof Date ? value : new Date(value))

// Building an Intl formatter is slow next to using one, and lists format many values.
const cache = new Map<string, any>()
const cached = <T>(kind: string, locale: string, options: object, build: () => T): T => {
  const id = `${kind}|${locale}|${JSON.stringify(options)}`
  if (!cache.has(id)) cache.set(id, build())
  return cache.get(id)
}

export const formatNumber = (value: number, options: Intl.NumberFormatOptions = {}, locale = currentLocale()) =>
  cached('number', locale, options, () => new Intl.NumberFormat(locale, options)).format(value)

/** 0.42 -> '42%' (fr: '42 %'). */
export const formatPercent = (fraction: number, options: Intl.NumberFormatOptions = {}, locale = currentLocale()) =>
  formatNumber(fraction, { style: 'percent', maximumFractionDigits: 0, ...options }, locale)

export type DateStyle = 'short' | 'medium' | 'long' | 'full' | 'monthDay' | 'monthYear' | 'weekday' | 'time'

const DATE_STYLES: Record<DateStyle, Intl.DateTimeFormatOptions> = {
  // en 10/20/26 | fr 20/10/2026
  short: { dateStyle: 'short' },
  // en Oct 20, 2026 | fr 20 oct. 2026
  medium: { dateStyle: 'medium' },
  // en October 20, 2026 | fr 20 octobre 2026
  long: { dateStyle: 'long' },
  // en Tuesday, October 20, 2026 | fr mardi 20 octobre 2026
  full: { dateStyle: 'full' },
  // en Oct 20 | fr 20 oct.
  monthDay: { month: 'short', day: 'numeric' },
  // en October 2026 | fr octobre 2026
  monthYear: { month: 'long', year: 'numeric' },
  // en Tuesday | fr mardi
  weekday: { weekday: 'long' },
  // en 3:05 PM | fr 15:05
  time: { timeStyle: 'short' },
}

/**
 * formatDate(date) or formatDate(date, 'long'). Pass Intl options as the third argument to add to
 * a style (for example { timeZone: 'UTC' }), or Intl options instead of a style.
 */
export const formatDate = (
  value: DateInput,
  style: DateStyle | Intl.DateTimeFormatOptions = 'medium',
  extra: Intl.DateTimeFormatOptions = {},
  locale = currentLocale(),
) => {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return ''
  const options = { ...(typeof style === 'string' ? DATE_STYLES[style] : style), ...extra }
  return cached('date', locale, options, () => new Intl.DateTimeFormat(locale, options)).format(date)
}

const ORDINAL_SUFFIX: Record<string, string> = { one: 'st', two: 'nd', few: 'rd', other: 'th' }

/**
 * The home page's date heading: 'Oct 22nd 2026' in English (what moment's 'MMM Do YYYY' printed),
 * the language's own short date elsewhere: de '22. Okt. 2026', fr '22 oct. 2026', ja '2026年10月22日'.
 */
export const todayHeading = (value: DateInput = new Date(), locale = currentLocale()) => {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return ''
  if (!locale.toLowerCase().startsWith('en')) return formatDate(date, { year: 'numeric', month: 'short', day: 'numeric' }, {}, locale)
  const day = date.getDate()
  const rule = cached('ordinal', 'en', {}, () => new Intl.PluralRules('en', { type: 'ordinal' })).select(day)
  return `${formatDate(date, { month: 'short' }, {}, 'en')} ${day}${ORDINAL_SUFFIX[rule]} ${date.getFullYear()}`
}

const RELATIVE_UNITS: { unit: Intl.RelativeTimeFormatUnit; seconds: number }[] = [
  { unit: 'year', seconds: 365 * 24 * 3600 },
  { unit: 'month', seconds: 30 * 24 * 3600 },
  { unit: 'week', seconds: 7 * 24 * 3600 },
  { unit: 'day', seconds: 24 * 3600 },
  { unit: 'hour', seconds: 3600 },
  { unit: 'minute', seconds: 60 },
  { unit: 'second', seconds: 1 },
]

/**
 * 'in 3 days', '2 hours ago', 'yesterday' (fr: 'dans 3 jours', 'il y a 2 heures', 'hier'). Picks
 * the largest unit that fits. `now` is there for tests.
 */
export const formatRelative = (
  value: DateInput,
  { now = new Date(), numeric = 'auto' }: { now?: DateInput; numeric?: 'auto' | 'always' } = {},
  locale = currentLocale(),
) => {
  const date = toDate(value)
  if (Number.isNaN(date.getTime())) return ''
  const diffSeconds = (date.getTime() - toDate(now).getTime()) / 1000
  const { unit, seconds } =
    RELATIVE_UNITS.find((candidate) => Math.abs(diffSeconds) >= candidate.seconds) ||
    RELATIVE_UNITS[RELATIVE_UNITS.length - 1]
  return formatRelativeValue(Math.round(diffSeconds / seconds), unit, { numeric }, locale)
}

/** formatRelativeValue(-1, 'day') -> 'yesterday'. */
export const formatRelativeValue = (
  value: number,
  unit: Intl.RelativeTimeFormatUnit,
  options: Intl.RelativeTimeFormatOptions = { numeric: 'auto' },
  locale = currentLocale(),
) => cached('relative', locale, options, () => new Intl.RelativeTimeFormat(locale, options)).format(value, unit)

/** ['A', 'B', 'C'] -> 'A, B, and C' (fr: 'A, B et C'). */
export const formatList = (
  items: string[],
  options: { type?: 'conjunction' | 'disjunction'; style?: 'long' | 'short' | 'narrow' } = {},
  locale = currentLocale(),
) => {
  const ListFormat = (Intl as any).ListFormat
  if (!ListFormat) return items.join(', ')
  return cached('list', locale, options, () => new ListFormat(locale, options)).format(items)
}
